"use client"

import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Checkbox } from "@/components/ui/checkbox";
import { useSelector, useDispatch } from "react-redux";

import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { useForm } from "react-hook-form";

import Link from "next/link";
import { Button, buttonVariants } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import RegularInput from "../../admin/form-components/regularInput";
import RegularSelect from "../../admin/form-components/regularSelect";
import { Textarea } from "@/components/ui/textarea";
import { SetStateAction, useEffect, useState } from "react";
import { findOneUserById, sendMailchimp } from "@/app/adapters/api";
import { selectEmailsStopped, selectLanguage } from "@/app/redux/store";
import { showAlert } from "@/app/redux/store";

const formSchema = z.object({
  email: z.string().email({
    message: "Por favor, introduce una dirección de correo electrónico válida",
  }),
  nombre: z.string().min(1, {
    message: "Campo obligatorio",
  }),
  apellido: z.string().min(1, {
    message: "Campo obligatorio",
  }),
  telefono: z.string().regex(/^[0-9]+$/, {
    message: "Por favor, introduce un número de teléfono válido",
  }),
  mensaje: z.string().min(1, {
    message: "Campo obligatorio",
  }),
  tipoConsulta: z.string().min(1, {
    message: "Campo obligatorio",
  }),
});

const ContactForm = () => {
  const [checkBoxPrivacy, setCheckBoxPrivacy] = useState(false);
  const [checkBoxNewsletter, setCheckBoxNewsletter] = useState(false);
  const lang = useSelector(selectLanguage);

  const dispatch = useDispatch();

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      email: "",
      nombre: "",
      apellido: "",
      telefono: "",
      mensaje: "",
      tipoConsulta: "",
    },
  });

  function sendMailchimpContact(formData: any) {
    let templateName = "";
    let subject = "";
    if (lang === "es") {
      templateName = "es-es-contact";
      subject = "Contacto" + formData.nombre + " " + formData.apellido;
    } else {
      templateName = "en-us-contact";
      subject = "Contact" + formData.nombre + " " + formData.apellido;
    }
    const data = {
      params: {
        to: "hello@goparity.com",
        templateName: templateName,
        subject: subject,
        from: "hello@goparity.com",
        vars: [
          { name: "name", content: formData.nombre + " " + formData.apellido },
          { name: "email", content: formData.email },
          { name: "phone", content: formData.telefono },
          { name: "message", content: formData.mensaje },
        ],
      },
    };
    sendMailchimp(data);
  }

  const handleCheckBoxPrivacyChange = (checked: any) => {
    setCheckBoxPrivacy(!checkBoxPrivacy);
  };

  const handleCheckBoxNewsletterChange = (checked: any) => {
    setCheckBoxNewsletter(!checkBoxNewsletter);
  };

  const onSubmit = async (data: any) => {
    const formData = {
      ...data,
      checkBoxNewsletter: checkBoxNewsletter,
    };

    try {
      await sendMailchimpContact(formData);
      const message =
        lang === "es"
          ? "Mensaje enviado correctamente"
          : "Message sent successfuly";
      dispatch(
        showAlert({
          text: message,
          type: "success",
          isShow: true,
        })
      );
    } catch (error) {
      const errorMessage =
        lang === "es"
          ? "Error al enviar el comentario"
          : "Error sending comment";
      dispatch(
        showAlert({
          text: errorMessage,
          type: "error",
          isShow: true,
        })
      );
      console.error("Error sending comment:", error);
    }
  };

  const handleFormSubmit = form.handleSubmit(onSubmit);

  return (
    <div className="flex flex-wrap justify-center items-center mx-auto mt-6 max-w-[920px]">
      <form onSubmit={handleFormSubmit} className="sm:w-5/6 w-11/12">
        <Form {...form}>
          <div className="flex sm:flex-row flex-col gap-4 mb-8">
            <div className="sm:w-6/12 w-full">
              <RegularInput
                {...form.register("nombre")}
                name="nombre"
                label="Nombre"
                extraClass="bg-white w-full"
                form={form}
                isEditable={true}
              />
              <RegularInput
                {...form.register("email")}
                name="email"
                label="Email"
                extraClass="bg-white w-full"
                form={form}
                isEditable={true}
              />
              <RegularSelect
                {...form.register("tipoConsulta")}
                form={form}
                label="Tipo de consulta"
                name="tipoConsulta"
                items={[
                  { value: "Inversor", label: "Inversor" },
                  { value: "Emprendedor", label: "Emprendedor" },
                ]}
                extraClass="bg-white w-full h-10"
              />
            </div>
            <div className="sm:w-6/12 w-full">
              <RegularInput
                {...form.register("apellido")}
                name="apellido"
                label="Apellido"
                extraClass="bg-white w-full"
                form={form}
                isEditable={true}
              />
              <RegularInput
                {...form.register("telefono")}
                name="telefono"
                label="Teléfono"
                extraClass="bg-white w-full"
                form={form}
                isEditable={true}
              />
            </div>
          </div>
          <Textarea
            className="w-full h-80 mb-8"
            {...form.register("mensaje")}
          />
          <div className="flex items-center gap-4 mb-2">
            <Checkbox
              checked={checkBoxPrivacy}
              onCheckedChange={handleCheckBoxPrivacyChange}
              className=""
            />
            <p>
              Acepto los{" "}
              <a className="text-primary hover:text-dark-primary" href="">
                {" "}
                terminos y condiciones de uso{" "}
              </a>{" "}
              de este sitio web
            </p>
          </div>
          <div className="flex items-center gap-4 mb-10">
            <Checkbox
              checked={checkBoxNewsletter}
              onCheckedChange={handleCheckBoxNewsletterChange}
              className=""
            />
            <p>
              Deseo que me mantengáis informado sobre las novedades de la Bolsa
              Social.
            </p>
          </div>
          <Button
            onClick={handleFormSubmit}
            size={"lg"}
            variant="orange"
            type="submit"
            className="mr-2 rounded-full"
            disabled={!checkBoxPrivacy}
          >
            ENVIAR
          </Button>
        </Form>
      </form>
    </div>
  );
};

export default ContactForm;
